'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { markdownRehypeSanitizePlugins } from '@/lib/markdown-rehype-plugins';
import type { MobileUiConfigArtifact, MobileUiConfigResponse } from '@/shared/mobile-service/api/cors-mobile-api';
import {
  citationConclusion,
  emptyHydratedArtifactMessage,
  evidenceCitationLabel,
  evidenceLocatorKey,
  formatEvidenceJson,
  hydratedArtifactMarkdown,
  hydratedEvidenceMarkdown,
  hydratedThreadMarkdown,
} from '@/components/mobile-citations/citation-evidence';
import type { HydratedArtifactState } from '@/components/mobile-citations/useHydratedArtifactEvidence';
import type { Source } from '@/shared/types';
import { mapPointIdField } from '@/shared/mobile-service/lib/build-map-query';
import { citationQuerySummary, citationTableColumns } from '@/shared/mobile-service/lib/citation-table';
import { resolveArtifactColumns } from '@/shared/mobile-service/lib/columns';
import { artifactBubbleView, artifactMapView, formatViewLabel } from '@/shared/mobile-service/lib/labels';
import {
  inferCitationMapView,
  mapRowsToPoints,
  rowsWithInferredCoordinates,
} from '@/shared/mobile-service/lib/map-points';
import { CitedMap } from './map/CitedMap';
import { CitedThread } from './messages/CitedThread';
import { SlideOverChromeProvider, APP_SHELL_SLIDE_OVER_CHROME } from './SlideOver';
import { CitedGroups } from './table/CitedGroups';
import { CitedTable } from './table/CitedTable';

type PaneView = 'markdown' | 'messages' | 'map' | 'table';

/**
 * When coordinates exist the map is the default. Otherwise messages, then a
 * table, then markdown. The query that produced the rows stays visible on every
 * outcome, including a single row, an empty result, and a load error.
 *
 * View choice is local and resets when the locator identity changes so switching
 * citations does not keep a previous selection.
 */
export function CitationHydratedPane({
  source,
  hydrated,
  artifact,
  uiConfig,
}: {
  source: Source;
  hydrated: HydratedArtifactState;
  artifact: MobileUiConfigArtifact | undefined;
  uiConfig: MobileUiConfigResponse | undefined;
}) {
  const t = useTimePilotT();
  const locatorKey = evidenceLocatorKey(source.evidence);
  const [userView, setUserView] = useState<PaneView | null>(null);
  useEffect(() => {
    setUserView(null);
  }, [locatorKey]);

  const messagesView = artifactBubbleView(artifact);
  const authoredMapView = artifactMapView(artifact);
  const locations = artifact?.locations;
  const mappedRows = useMemo(
    () => rowsWithInferredCoordinates(hydrated.items),
    [hydrated.items],
  );
  const mapView = useMemo(
    () => inferCitationMapView(authoredMapView, hydrated.displayFields, mappedRows, locations),
    [authoredMapView, hydrated.displayFields, mappedRows, locations],
  );
  const mapPoints = useMemo(
    () =>
      mapView
        ? mapRowsToPoints(mappedRows, mapView, mapPointIdField(artifact), 0, locations)
        : [],
    [mappedRows, mapView, artifact, locations],
  );
  const kind = source.evidence?.kind;
  const mobileAcquisitionId = source.evidence?.mobileAcquisitionId;
  const queryShape = source.evidence?.queryShape;
  const queryLike = kind === 'query' || kind === 'absence';
  const isGroupBy = queryShape === 'groupBy';
  const isRollup = queryShape === 'aggregate' || queryShape === 'count' || isGroupBy;
  const rowCount = hydrated.items.length;
  const singleRow = rowCount === 1;
  const canShowMessages =
    kind !== 'absence' &&
    !isRollup &&
    hydrated.item != null &&
    artifact != null &&
    messagesView != null;
  const canShowMap = !isGroupBy && mapView != null && mapPoints.length > 0;
  const canShowTable =
    isGroupBy ||
    (!singleRow &&
      mobileAcquisitionId != null &&
      citationTableColumns(artifact, uiConfig) != null);
  const fallbackView: PaneView = canShowMessages ? 'messages' : canShowTable ? 'table' : 'markdown';
  const defaultView: PaneView = canShowMap ? 'map' : fallbackView;
  const options: PaneView[] = [defaultView];
  const pushOption = (name: PaneView) => {
    if (!options.includes(name)) options.push(name);
  };
  if (canShowMap) pushOption('map');
  if (canShowMessages) pushOption('messages');
  if (canShowTable) pushOption('table');
  if (fallbackView === 'markdown' && defaultView !== 'markdown') pushOption('markdown');
  const active: PaneView = userView && options.includes(userView) ? userView : defaultView;
  const requestedViewRef = useRef<string | null>(null);
  useEffect(() => {
    requestedViewRef.current = null;
  }, [locatorKey]);

  useEffect(() => {
    const request = (requestedView: string) => {
      if (requestedViewRef.current === requestedView) return;
      requestedViewRef.current = requestedView;
      hydrated.setPage({ offset: 0, requestedView });
    };
    if (userView == null) {
      if (defaultView === 'map' && queryLike) {
        request('map');
      }
      return;
    }
    if (userView === 'messages') {
      request('messages');
      return;
    }
    if (!queryLike) return;
    if (userView === 'map' || userView === 'table') {
      request(userView);
    }
  }, [queryLike, userView, defaultView, hydrated.setPage]);
  const fieldLabels = useMemo(() => {
    if (!artifact || !uiConfig) return undefined;
    const resolved = resolveArtifactColumns(artifact.graphql_type, uiConfig);
    if (!resolved || resolved.columns.length === 0) return undefined;
    return new Map(resolved.columns.map((column) => [column.field, column.label]));
  }, [artifact, uiConfig]);
  const label = evidenceCitationLabel(source);
  const conclusion = citationConclusion(source);
  const querySummary = (
    <CitationQuerySummary evidence={source.evidence} fieldLabels={fieldLabels} uiConfig={uiConfig} />
  );
  const conclusionLine = conclusion ? <CitationConclusion text={conclusion} /> : null;

  if (hydrated.status === 'error' && hydrated.item == null) {
    return (
      <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-2">
        {querySummary}
        {conclusionLine}
        <CitationFallback text={hydrated.errorMessage} />
      </div>
    );
  }
  if (hydrated.status === 'empty' && hydrated.item == null) {
    return (
      <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-2">
        {querySummary}
        {conclusionLine}
        <CitationFallback text={emptyHydratedArtifactMessage(source)} />
      </div>
    );
  }
  if (hydrated.item == null) {
    return conclusionLine;
  }

  const markdown =
    (hydrated.display === 'markdown' ? hydratedEvidenceMarkdown(hydrated.item) : null) ??
    (hydrated.display === 'thread'
      ? hydratedThreadMarkdown(hydrated.items, hydrated.displayFields, hydrated.focusIds, label)
      : null) ??
    hydratedArtifactMarkdown(hydrated.item, label);

  return (
    // Unlike the standalone viewer, this pane renders under the Assistant
    // sub-header, so its slide-overs portal clear of that bar.
    <SlideOverChromeProvider value={APP_SHELL_SLIDE_OVER_CHROME}>
      <div
        data-testid="CitationHydratedPane"
        className={`flex min-h-0 min-w-0 w-full flex-1 flex-col gap-2 ${
          isGroupBy ? 'overflow-y-auto' : 'overflow-hidden'
        }`}
      >
        {querySummary}
        {conclusionLine}
        {options.length > 1 ? (
          <div
            role="group"
            aria-label={t("viewers.view")}
            data-testid="CitationViewSwitcher"
            className="flex shrink-0 items-center gap-1 self-end rounded-md border border-white/20 bg-white/5 p-0.5"
          >
            {options.map((name) => {
              const selected = name === active;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setUserView(name)}
                  aria-pressed={selected}
                  className={`rounded px-2.5 py-1 text-sm transition-colors ${
                    selected
                      ? 'bg-brand-blue/25 font-medium text-brand-blue-light'
                      : 'text-white/70 hover:bg-white/10'
                  }`}
                >
                  {formatViewLabel({ name }, t)}
                </button>
              );
            })}
          </div>
        ) : null}

        {active === 'messages' && messagesView && artifact ? (
          <CitedThread
            rows={hydrated.items}
            view={messagesView}
            artifact={artifact}
            focusIds={hydrated.focusIds}
            highlightIds={hydrated.highlightIds}
            viewerHref={hydrated.viewerHref}
            label={label}
            mobileAcquisitionId={
              typeof mobileAcquisitionId === 'number' ? mobileAcquisitionId : null
            }
          />
        ) : active === 'map' && mapView ? (
          <CitedMap
            rows={mappedRows}
            view={mapView}
            artifact={artifact}
            focusIds={hydrated.focusIds}
            highlightIds={hydrated.highlightIds}
            viewerHref={hydrated.viewerHref}
            label={label}
          />
        ) : active === 'table' && isGroupBy ? (
          <CitedGroups
            rows={hydrated.items}
            label={label}
            evidence={source.evidence}
            total={hydrated.total}
            offset={hydrated.offset}
            onOffsetChange={(next) => hydrated.setPage({ offset: next })}
          />
        ) : active === 'table' && artifact && uiConfig && mobileAcquisitionId != null ? (
          <CitedTable
            rows={hydrated.items}
            artifact={artifact}
            uiConfig={uiConfig}
            mobileAcquisitionId={mobileAcquisitionId}
            focusIds={hydrated.focusIds}
            viewerHref={hydrated.viewerHref}
            label={label}
            total={queryLike ? hydrated.total : undefined}
            offset={queryLike ? hydrated.offset : undefined}
            onPageChange={
              queryLike
                ? (page) => hydrated.setPage({ ...page, requestedView: 'table' })
                : undefined
            }
          />
        ) : markdown != null ? (
          <div
            data-testid="CitationEvidenceMarkdown"
            className="m-0 min-h-0 flex-1 overflow-y-auto break-words rounded-md bg-white/5 p-3 text-[12px] text-white/90"
          >
            <ReactMarkdown
              rehypePlugins={markdownRehypeSanitizePlugins}
              components={{
                h1: ({ children }) => (
                  <h1 className="m-0 pb-2 text-[14px] font-semibold text-white">{children}</h1>
                ),
                p: ({ children }) => (
                  <p className="m-0 pb-1.5 text-[12px] font-light leading-snug text-white/90">
                    {children}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-white">{children}</strong>
                ),
              }}
            >
              {markdown}
            </ReactMarkdown>
          </div>
        ) : (
          <CitationFallback text={formatEvidenceJson(source.evidence ?? {})} />
        )}
      </div>
    </SlideOverChromeProvider>
  );
}

export function CitationConclusion({ text }: { text: string }) {
  return (
    <p data-testid="CitationQueryConclusion" className="m-0 shrink-0 text-sm text-white/90">
      {text}
    </p>
  );
}

function CitationQuerySummary({
  evidence,
  fieldLabels,
  uiConfig,
}: {
  evidence: Source['evidence'];
  fieldLabels?: ReadonlyMap<string, string>;
  uiConfig?: MobileUiConfigResponse;
}) {
  const lines = citationQuerySummary(evidence, { fieldLabels, uiConfig });
  if (lines.length === 0) return null;
  return (
    <div
      data-testid="CitationQuerySummary"
      className="shrink-0 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-2"
    >
      {lines.map((line) => (
        <p key={line} className="m-0 break-all text-xs text-amber-100/80">
          {line}
        </p>
      ))}
    </div>
  );
}

function CitationFallback({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <pre
      data-testid="CitationEvidenceJson"
      className="m-0 min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap break-all rounded-md bg-white/5 p-3 text-[12px] text-white/90"
    >
      {text}
    </pre>
  );
}
